import { Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { APP_DIALOG_DATA } from '../../../shared/components/dialog/dialog.tokens';
import { AppDialogRef } from '../../../shared/components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../../shared/components/dialog/dialog.imports';
import { DialogService } from '../../../shared/components/dialog/dialog.service';
import { ApiService } from '../../../_services/api.service';
import { AnalyticsService } from '../../../_services/analytics.service';
import { CopilotRecipient, CopilotRecipients } from '../../../_interfaces/document';
import {
  ccRecipients,
  cloneRecipients,
  isManualRecipient,
  isPresentRecipient,
  matchesRecipientQuery,
  sortRecipientsByName,
} from './recipient.helpers';
import { RecipientManualDialogComponent } from './recipient-manual.component';
import { RecipientDetailsComponent } from './recipient-details.component';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';

const DYNAMIC_RECIPIENT_TYPES = new Set([
  'consultant doctor',
  'admitting doctor',
  'attending doctor',
  'referring doctor',
  'primary care provider',
  'patient',
  'next of kin',
]);

function dynamicRecipientType(recipient: CopilotRecipient): string | null {
  const type = recipient.recipient_type?.trim().toLowerCase();
  return type && DYNAMIC_RECIPIENT_TYPES.has(type) ? type : null;
}

export interface RecipientDialogData {
  mode: 'primary' | 'cc';
  recipients: CopilotRecipients;
  viewOnly?: boolean;
  dictationId?: string;
  templateId?: number;
  disableFreeText?: boolean;
}

@Component({
  selector: 'app-recipient-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    TranslateModule,
    ...DIALOG_IMPORTS,
    RecipientDetailsComponent,
    SkeletonComponent,
  ],
  templateUrl: './recipient-dialog.component.html',
  host: {
    class: 'flex min-h-0 max-h-[calc(100dvh-3rem)] flex-col overflow-y-auto',
  },
})
export class RecipientDialogComponent implements OnInit {
  readonly searchControl = new FormControl('', { nonNullable: true });
  @ViewChild('recipientList') recipientList?: ElementRef<HTMLElement>;
  mode: 'primary' | 'cc';
  draft: CopilotRecipients;
  results: CopilotRecipient[] = [];
  isLoading = false;
  readonly skeletonOptions = [
    { name: '42%', role: '28%', address: '68%' },
    { name: '55%', role: '34%', address: '48%' },
  ];

  private patientOptions: CopilotRecipient[] | null = null;
  private optionRows: CopilotRecipient[] = [];
  private showingAddressBook = false;
  private activeQuery = '';
  private listRequest = 0;
  private selectedPrimaryIndex: number | null = null;
  private selectedCcIndexes = new Set<number>();
  private ccDraftByIndex = new Map<number, CopilotRecipient>();

  constructor(
    private dialogRef: AppDialogRef<RecipientDialogComponent, CopilotRecipients | undefined>,
    @Inject(APP_DIALOG_DATA) public data: RecipientDialogData,
    private apiService: ApiService,
    private dialog: DialogService,
    private translate: TranslateService,
    @Optional() private analytics: AnalyticsService | null = null,
  ) {
    this.draft = cloneRecipients(data.recipients);
    this.mode = data.mode;
  }

  get isPrimary(): boolean {
    return this.mode === 'primary';
  }

  get title(): string {
    if (this.data.viewOnly) {
      return this.translate.instant('Selected recipients');
    }
    return this.isPrimary
      ? this.translate.instant('Select primary recipient')
      : this.translate.instant('Select cc recipient');
  }

  get selectedTabIndex(): number {
    return this.isPrimary ? 0 : 1;
  }

  isPrimarySelected(index: number): boolean {
    return this.selectedPrimaryIndex === index;
  }

  get canEdit(): boolean {
    return !this.data.viewOnly;
  }

  get selectedPrimary(): CopilotRecipient | null {
    return isPresentRecipient(this.draft.primaryRecipient) ? this.draft.primaryRecipient : null;
  }

  get selectedCc(): CopilotRecipient[] {
    return sortRecipientsByName(ccRecipients(this.draft));
  }

  get canUseFreeText(): boolean {
    return this.canEdit && !this.data.disableFreeText;
  }

  ngOnInit(): void {
    if (this.data.viewOnly) return;
    void this.loadDefaultOptions();
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.canEdit) return;
    this.dialogRef.close(this.draft);
  }

  onTabChange(index: number): Promise<void> | void {
    if (index !== 0 && index !== 1) return;
    const next = index === 0 ? 'primary' : 'cc';
    if (next === this.mode) return;
    this.mode = next;
    this.scrollListToTop();
    this.analytics?.track('recipient_tab_switched', {
      surface: 'web_document',
      ...this.blueprintIdProps(),
      to_tab: next,
    });
    this.searchControl.setValue('');
    return this.loadDefaultOptions();
  }

  submitSearch(): void {
    const query = this.searchControl.value.trim();
    if (query.length === 1) return;
    if (!query) {
      void this.loadDefaultOptions();
      return;
    }
    void this.search();
  }

  onSearchInput(): void {
    if (this.searchControl.value.trim() || !this.activeQuery) return;
    void this.loadDefaultOptions();
  }

  selectPrimary(index: number): void {
    if (!this.canEdit || !this.results[index]) return;
    if (this.selectedPrimaryIndex === index) {
      this.draft.primaryRecipient = null;
      this.selectedPrimaryIndex = null;
      return;
    }
    this.selectedPrimaryIndex = index;
    this.assignPrimary(index);
  }

  onPrimaryKeydown(index: number, event: KeyboardEvent): void {
    if (!this.isRowActivation(event)) return;
    this.selectPrimary(index);
  }

  /** Keyboard activation reports through the radio; it only selects. */
  onPrimaryRadioChange(index: number): void {
    if (!this.canEdit || !this.results[index]) return;
    this.selectedPrimaryIndex = index;
    this.assignPrimary(index);
  }

  private assignPrimary(index: number): void {
    const next = { ...this.results[index], changed: true };
    this.draft.primaryRecipient = next;
    this.results = this.results.map((item, itemIndex) => (itemIndex === index ? next : item));
  }

  isCcSelected(index: number): boolean {
    return this.selectedCcIndexes.has(index);
  }

  toggleCc(index: number, checked: boolean): void {
    if (!this.canEdit || !this.results[index]) return;
    const current = ccRecipients(this.draft);
    if (checked) {
      if (this.selectedCcIndexes.has(index)) return;
      const row = this.results[index];
      const entry = current.includes(row) ? row : { ...row };
      if (!current.includes(entry)) {
        this.draft.ccRecipients = [...current, entry];
      }
      if (entry !== row) {
        this.results = this.results.map((item, itemIndex) => (itemIndex === index ? entry : item));
      }
      this.selectedCcIndexes.add(index);
      this.ccDraftByIndex.set(index, entry);
      return;
    }
    const entry = this.ccDraftByIndex.get(index) ?? this.results[index];
    this.selectedCcIndexes.delete(index);
    this.ccDraftByIndex.delete(index);
    this.draft.ccRecipients = current.filter((item) => item !== entry);
  }

  onCcKeydown(index: number, event: KeyboardEvent): void {
    if (!this.isRowActivation(event)) return;
    this.toggleCc(index, !this.isCcSelected(index));
  }

  private isRowActivation(event: KeyboardEvent): boolean {
    if (event.target !== event.currentTarget) return false;
    if (event.key !== 'Enter' && event.key !== ' ') return false;
    event.preventDefault();
    return true;
  }

  async addManually(): Promise<void> {
    if (!this.canUseFreeText) return;
    const ref = this.dialog.open(RecipientManualDialogComponent, {
      size: 'md',
      presentation: 'dialog',
      tone: 'container-low',
      hidePrevious: false,
      data: { mode: this.mode, templateId: this.data.templateId },
    });
    const result = await firstValueFrom(ref.afterClosed());
    this.dialog.show(this.dialogRef);
    if (!result) return;
    if (this.isPrimary) {
      this.draft.primaryRecipient = result;
    } else {
      this.draft.ccRecipients = [...ccRecipients(this.draft), result];
    }
    if (this.showingAddressBook) return;
    this.results = this.withSavedFirst(this.optionRows);
    this.syncCheckedIndexes();
  }

  isManualRecipient = isManualRecipient;

  async editManually(index: number, event: Event): Promise<void> {
    event.stopPropagation();
    const recipient = this.results[index];
    if (!this.canUseFreeText || !recipient || !isManualRecipient(recipient)) return;
    const ref = this.dialog.open(RecipientManualDialogComponent, {
      size: 'md',
      presentation: 'dialog',
      tone: 'container-low',
      hidePrevious: false,
      data: {
        mode: this.mode,
        templateId: this.data.templateId,
        initialContent: recipient.content ?? '',
      },
    });
    const result = await firstValueFrom(ref.afterClosed());
    this.dialog.show(this.dialogRef);
    if (!result) return;
    if (this.isPrimary) {
      if (this.draft.primaryRecipient === recipient || this.selectedPrimaryIndex === index) {
        this.draft.primaryRecipient = result;
      }
    } else {
      const draftEntry = this.ccDraftByIndex.get(index) ?? recipient;
      this.draft.ccRecipients = ccRecipients(this.draft).map((item) =>
        item === draftEntry ? result : item,
      );
    }
    if (this.showingAddressBook) {
      this.results = this.results.map((item, itemIndex) => (itemIndex === index ? result : item));
      this.syncCheckedIndexes();
      return;
    }
    this.results = this.withSavedFirst(this.optionRows);
    this.syncCheckedIndexes();
  }

  async loadDefaultOptions(): Promise<void> {
    if (this.data.viewOnly) return;
    const requestId = ++this.listRequest;
    this.activeQuery = '';
    this.isLoading = true;
    try {
      const rows = [...(await this.loadPatientOptions())];
      if (requestId !== this.listRequest) return;
      this.showDefaultList(rows);
    } catch {
      if (requestId !== this.listRequest) return;
      this.showDefaultList([]);
    } finally {
      if (requestId === this.listRequest) this.isLoading = false;
    }
  }

  async search(): Promise<void> {
    if (this.data.viewOnly) return;
    const query = this.searchControl.value.trim();
    if (query.length < 2) return;
    const requestId = ++this.listRequest;
    this.activeQuery = query;
    this.isLoading = true;
    try {
      const bookRows = await this.apiService.listScribeRecipientOptions({ search: query });
      if (requestId !== this.listRequest) return;
      if (bookRows.length) {
        this.showAddressBook(bookRows);
      } else {
        const patientOptions = await this.loadPatientOptions();
        if (requestId !== this.listRequest) return;
        this.showAddressBook(
          patientOptions.filter((recipient) => matchesRecipientQuery(recipient, query)),
        );
      }
      this.analytics?.track('recipient_search_performed', {
        surface: 'web_document',
        ...this.blueprintIdProps(),
        recipient_type: this.mode,
        result_count: this.results.length,
      });
    } catch {
      if (requestId !== this.listRequest) return;
      this.showAddressBook([]);
      this.analytics?.track('recipient_load_failed', {
        surface: 'web_document',
        ...this.blueprintIdProps(),
        request_type: 'address_book_search',
      });
    } finally {
      if (requestId === this.listRequest) this.isLoading = false;
    }
  }

  private showAddressBook(rows: CopilotRecipient[]): void {
    this.showingAddressBook = true;
    this.optionRows = rows;
    this.results = sortRecipientsByName(rows);
    this.clearCheckedIndexes();
  }

  private showDefaultList(rows: CopilotRecipient[]): void {
    this.showingAddressBook = false;
    this.optionRows = rows;
    this.results = this.withSavedFirst(rows);
    this.syncCheckedIndexes();
  }

  private withSavedFirst(rows: CopilotRecipient[]): CopilotRecipient[] {
    const selected = this.selectedRecipients();
    const selectedTypes = new Set(
      selected
        .map((item) => dynamicRecipientType(item))
        .filter((type): type is string => !!type),
    );
    const rest = rows.filter((row) => {
      const type = dynamicRecipientType(row);
      return !type || !selectedTypes.has(type);
    });
    return [...sortRecipientsByName(selected), ...sortRecipientsByName(rest)];
  }

  private clearCheckedIndexes(): void {
    this.selectedPrimaryIndex = null;
    this.selectedCcIndexes.clear();
    this.ccDraftByIndex.clear();
  }

  private syncCheckedIndexes(): void {
    this.clearCheckedIndexes();
    if (this.isPrimary) {
      const primary = this.draft.primaryRecipient;
      const index = primary ? this.results.indexOf(primary) : -1;
      this.selectedPrimaryIndex = index >= 0 ? index : null;
      return;
    }
    ccRecipients(this.draft).forEach((item) => {
      const index = this.results.indexOf(item);
      if (index < 0) return;
      this.selectedCcIndexes.add(index);
      this.ccDraftByIndex.set(index, item);
    });
  }

  private selectedRecipients(): CopilotRecipient[] {
    if (this.isPrimary) {
      return isPresentRecipient(this.draft.primaryRecipient) ? [this.draft.primaryRecipient!] : [];
    }
    return ccRecipients(this.draft);
  }

  private async loadPatientOptions(): Promise<CopilotRecipient[]> {
    if (this.patientOptions) return this.patientOptions;
    if (!this.data.dictationId) {
      this.patientOptions = [];
      return [];
    }
    try {
      this.patientOptions = await this.apiService.listScribeRecipientOptions({
        dictationId: this.data.dictationId,
        templateId: this.data.templateId,
      });
    } catch {
      this.patientOptions = [];
      this.analytics?.track('recipient_load_failed', {
        surface: 'web_document',
        ...this.blueprintIdProps(),
        request_type: 'defaults',
      });
    }
    return this.patientOptions;
  }

  private scrollListToTop(): void {
    let node: HTMLElement | null = this.recipientList?.nativeElement ?? null;
    while (node) {
      node.scrollTop = 0;
      if (node.classList.contains('app-dialog-surface')) break;
      const parent = node.parentElement;
      if (!parent || parent === document.body || parent === document.documentElement) break;
      node = parent;
    }
  }

  // Analytics helpers
  private blueprintIdProps(): { blueprint_id?: string } {
    return this.data.templateId != null ? { blueprint_id: String(this.data.templateId) } : {};
  }
}
